import React, { useEffect, useMemo, useRef } from "react";
import { Button, Typography, useMediaQuery } from "@material-ui/core";
import { ArrowDropDownOutlined } from "@material-ui/icons";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import MenuItem from "@material-ui/core/MenuItem";
import { usePopupState, bindMenu, bindHover } from "material-ui-popup-state/hooks";

import classes from "@css/components/website/Header.module.css";
import history from "../../history";
import { KustomerLauncher } from "../KustomerLauncher";
import { useSelector } from "react-redux";
import { roles } from "../../constants/roles";
import { segmentTrack } from "../../helpers/segment";

interface MenuItemType {
  label: string;
  link?: string;
  func?: () => void;
  icon?: React.ReactNode;
  isOutsideLink?: boolean;
  requiresRefresh?: boolean;
  column?: number;
  subLinks?: Array<{
    label: string;
    link: string;
  }>;
}

interface HeaderMenuItemProps {
  name: string;
  menuItems?: MenuItemType[];
  isOpaque: boolean;
  icon?: string;
  chipText?: string;
  linkTo?: string;
  extended?: boolean;
  showDropdownArrow?: boolean;
}

const MOBILE_BREAKPOINT = "(max-width:1369px)";

function HeaderMenuItem({ name, menuItems = [], isOpaque, icon, chipText, linkTo, extended = false, showDropdownArrow = true }: HeaderMenuItemProps) {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isLoggedIn = useSelector((state: any) => state.firebase.auth?.uid);
  const profile = useSelector((state: any) => state.firebase.profile);

  const isCustomer = isLoggedIn && profile?.userType === roles.customer.value;

  const popupState = usePopupState({
    variant: "popover",
    popupId: `headerMenu-${name.replace(/\s+/g, "-").toLowerCase()}`,
  });

  const menuItemsByColumn = useMemo(() => {
    if (!extended || !menuItems.length) return {};

    return menuItems.reduce((acc: Record<number, MenuItemType[]>, item) => {
      if (item.column) {
        if (!acc[item.column]) {
          acc[item.column] = [];
        }
        acc[item.column].push(item);
      }
      return acc;
    }, {});
  }, [extended, menuItems]);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    popupState.open();
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      popupState.close();
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobile && popupState.isOpen) {
      popupState.close();
    }
  }, [isMobile]);

  const handleItemClick = (item: MenuItemType) => {
    segmentTrack("Header Menu Item Clicked", {
      item: item.label,
      link: item.link,
      menuName: name,
    });

    if (item.func) {
      item.func();
    } else if (item.link) {
      if (item.isOutsideLink || item.requiresRefresh) {
        window.open(item.link, "_blank");
      } else {
        history.push(item.link);
      }
    }

    popupState.close();
  };

  const handleNameClick = () => {
    if (linkTo && linkTo !== "undefined") {
      history.push(linkTo);
    }
  };

  const getButtonClassName = () => {
    if (icon && !isCustomer) return classes.menuIconButton;
    if (icon && isCustomer) return classes.menuAvatarButton;
    return classes.menuButton;
  };

  const getButtonStyles = () => ({
    color: isOpaque ? "black" : "white",
    backgroundColor: "transparent !important",
  });

  const hasMenuItems = menuItems.length > 0;
  const isClickableName = linkTo && linkTo !== "undefined";

  const DropdownIcon = ({ isOpen }: { isOpen?: boolean }) => (
    <ArrowDropDownOutlined
      style={{
        transform: isOpen ? "rotate(-180deg)" : "rotate(0deg)",
        transition: "transform 0.2s ease",
      }}
      htmlColor={isOpaque ? "black" : "white"}
    />
  );

  const Chip = () => {
    if (!chipText) return null;

    return (
      <div className={classes.chipOuter}>
        <Typography className={classes.chipText}>{chipText}</Typography>
      </div>
    );
  };

  const ButtonContent = () => (
    <>
      {icon && (
        <img
          src={icon}
          alt={`${name} icon`}
          style={{
            filter: !isOpaque && name === "Support" ? "brightness(0) invert(1)" : "none",
          }}
        />
      )}
      {name && (
        <span
          onClick={isClickableName ? handleNameClick : undefined}
          style={{
            cursor: isClickableName ? "pointer" : "default",
            marginLeft: icon ? "8px" : "0",
            marginRight: hasMenuItems && showDropdownArrow ? "4px" : "0",
          }}
        >
          {name}
        </span>
      )}
      {hasMenuItems && showDropdownArrow && <DropdownIcon isOpen={popupState.isOpen} />}
    </>
  );

  const ExtendedMenu = () => (
    <div className={classes.extendedMenu}>
      {Object.keys(menuItemsByColumn).map((column) => (
        <div key={column}>
          {menuItemsByColumn[Number(column)].map((item) => (
            <div className={classes.menuItemParent} key={item.label}>
              <MenuItem className={classes.menuItem} onClick={() => handleItemClick(item)}>
                {item.label}
              </MenuItem>
              {item.subLinks && (
                <div className={classes.menuSubLinks}>
                  {item.subLinks.map((subLink) => (
                    <MenuItem className={classes.subMenuItem} key={subLink.label} onClick={() => handleItemClick(subLink)}>
                      {subLink.label}
                    </MenuItem>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const StandardMenu = () => (
    <>
      {menuItems.map((item) => (
        <MenuItem className={classes.menuItem} key={item.label} onClick={() => handleItemClick(item)}>
          {item.icon && <span style={{ marginRight: "16px" }}>{item.icon}</span>}
          <span>{!item.link ? <KustomerLauncher>{item.label}</KustomerLauncher> : item.label}</span>
        </MenuItem>
      ))}
    </>
  );

  return (
    <div
      style={{ position: chipText ? "relative" : undefined }}
      onMouseEnter={hasMenuItems ? handleMouseEnter : undefined}
      onMouseLeave={hasMenuItems ? handleMouseLeave : undefined}
    >
      <Button
        disableRipple
        disableFocusRipple
        disableTouchRipple
        className={getButtonClassName()}
        style={getButtonStyles()}
        {...(hasMenuItems ? bindHover(popupState) : {})}
        aria-label={name || "Menu"}
        aria-expanded={hasMenuItems ? popupState.isOpen : undefined}
        aria-haspopup={hasMenuItems}
      >
        <ButtonContent />
      </Button>

      {hasMenuItems && (
        <HoverMenu
          {...bindMenu(popupState)}
          anchorOrigin={extended ? { vertical: "bottom", horizontal: "left" } : { vertical: "bottom", horizontal: "right" }}
          transformOrigin={extended ? { vertical: "bottom", horizontal: "left" } : { vertical: "bottom", horizontal: "right" }}
          classes={{ paper: classes.menuDropdown }}
          open={popupState.isOpen}
        >
          {extended ? <ExtendedMenu /> : <StandardMenu />}
        </HoverMenu>
      )}

      <Chip />
    </div>
  );
}

export default HeaderMenuItem;
